import { Suspense, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { logout } from '@/features/auth/authThunks';
import { LogOut, Menu } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet.tsx';
import SidebarPanel from '@/components/layout/SidebarPanel.tsx';
import InfosUpdateForm from '../pages/dashboard/InfosUpdateForm';
import type { AuthUser } from '@/features/auth/types';
import { Skeleton } from '../ui/skeleton';
import { selectIsFreeWithoutLiveAccess } from '@/features/workspace/workspaceSlice';
import FreePlanUpsellBanner from '@/components/billing/FreePlanUpsellBanner';
import HelpsIcon from '@/components/icons/HelpsIcon';
import { isPhoneNumberMissing } from '@/lib/phoneNumberRequirement';

const HELP_CENTER_URL = 'https://help.welya.io/fr/';

export default function Topbar() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((s: RootState) => s.auth.user);
  const loading = useAppSelector((s: RootState) => s.auth.loading);
  const showFreeUpsellBanner = useAppSelector(selectIsFreeWithoutLiveAccess);
  const [open, setOpen] = useState(false);
  const [avatarSheetOpen, setAvatarSheetOpen] = useState(false);
  const phoneRequired = isPhoneNumberMissing(user);
  const handleAvatarSheetOpenChange = useCallback(
    (next: boolean) => {
      if (!next && phoneRequired) return;
      setAvatarSheetOpen(next);
    },
    [phoneRequired],
  );
  const initials = useMemo(() => {
    const first = (user?.firstName?.[0] ?? '').toUpperCase();
    const last = (user?.lastName?.[0] ?? '').toUpperCase();
    return (first + last || 'U').slice(0, 2);
  }, [user]);

  const handleLogout = useCallback(async () => {
    if (loading) return;
    const res = await dispatch(logout());
    if (logout.fulfilled.match(res)) {
      navigate('/login', { replace: true });
    }
  }, [dispatch, loading, navigate]);

  return (
    <header className="sticky top-0 z-10 bg-white">
      <div className="relative mx-auto flex h-16 items-center justify-between px-4 md:px-8">
        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/15 text-gray-800 hover:bg-white/10"
                aria-label={t('navigation.open')}
              >
                <Menu className="size-6 cursor-pointer" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-2">
              <SheetHeader className="sr-only">
                <SheetTitle>{t('navigation.title')}</SheetTitle>
                <SheetDescription>{t('navigation.description')}</SheetDescription>
              </SheetHeader>
              <SidebarPanel />
            </SheetContent>
          </Sheet>
        </div>
        {showFreeUpsellBanner && (
          <div className="hidden min-w-0 flex-1 justify-center px-4 md:flex">
            <FreePlanUpsellBanner />
          </div>
        )}

        <span className="hidden md:block text-sm text-foreground/70 select-none">
          {/*TODO : Put today date here*/}
        </span>

        <div className="flex items-center gap-3">
          {/* <LangSwitcher /> */}

          <a
            href={HELP_CENTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap text-foreground/70 transition-colors hover:bg-foreground/5 hover:text-foreground"
          >
            <HelpsIcon className="size-5 shrink-0" color="currentColor" />
            {t('topbar.helpCenter')}
          </a>

          <Sheet open={avatarSheetOpen || phoneRequired} onOpenChange={handleAvatarSheetOpenChange}>
            <SheetTrigger asChild>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label={t('navigation.openUserMenu')}
                  className="outline-none cursor-pointer"
                >
                  <Avatar className="size-8">
                    <AvatarImage
                      src={user?.pictureUrl}
                      alt={user?.firstName ?? t('navigation.userAvatarAlt')}
                    />
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </div>
            </SheetTrigger>

            <SheetContent
              side="right"
              className="w-full lg:max-w-196 border-none sm:rounded-l-[20px] bg-white"
              onOpenAutoFocus={(e) => {
                e.preventDefault();
              }}
              onEscapeKeyDown={(e) => {
                if (phoneRequired) e.preventDefault();
              }}
              onInteractOutside={(e) => {
                if (phoneRequired) {
                  e.preventDefault();
                  return;
                }
                const target = e.target as HTMLElement | null;
                if (!target) return;
                if (
                  target.closest('.Toastify__toast-container') ||
                  target.closest('.Toastify__toast')
                ) {
                  e.preventDefault();
                }
              }}
              onPointerDownOutside={(e) => {
                if (phoneRequired) {
                  e.preventDefault();
                  return;
                }
                const target = (e as unknown as { target: EventTarget | null })
                  .target as HTMLElement | null;
                if (!target) return;
                if (
                  target.closest('.Toastify__toast-container') ||
                  target.closest('.Toastify__toast')
                ) {
                  e.preventDefault();
                }
              }}
            >
              <SheetHeader className="sr-only">
                <SheetTitle>{t('navigation.userProfileTitle')}</SheetTitle>
                <SheetDescription>{t('navigation.userProfileDescription')}</SheetDescription>
              </SheetHeader>

              <Suspense fallback={<Skeleton className="w-full h-full rounded-lg" />}>
                <InfosUpdateForm
                  user={user as AuthUser}
                  phoneRequired={phoneRequired}
                  onClose={() => setAvatarSheetOpen(false)}
                />
              </Suspense>
              <div className="mt-auto px-4 pb-4 sm:px-6 sm:pb-6">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setAvatarSheetOpen(false);
                    void handleLogout();
                  }}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-destructive px-4 text-base font-medium text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
                >
                  <LogOut className="size-5" />
                  {t('auth.logout', { defaultValue: 'Se déconnecter' })}
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {showFreeUpsellBanner && <FreePlanUpsellBanner className="mx-4 mb-2 md:hidden" />}
    </header>
  );
}
